import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import SessionStorage from "../../../../service/storage/SessionStorage";
import StorageKeys from "../../../../const/StorageKeys";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import { SignupResult } from "../../../../model/onboarding/signup/SignupModel";
import Loader from "../../../../widget/common/ui/Loader";
import AppTranslation from "../../../../service/app_translation/AppTranslation";
import ShowToast from "../../../../widget/common/ui/Toast";
import SignupAccountVerificationProvider from "./SignupAccountVerificationProvider";
import { useDispatch } from "react-redux";
import { resetCountDown } from "../../../../libraries/state_reducers/widget/CountdownReducer";
import { getSecondsDiff } from "../../../../service/date_service/date_service";
import { appRoutes } from "../../../../routes/routes";
import { SignupProfileModel } from "../../../../model/onboarding/signup/SignupProfileModel";
import { AlertConfirm } from "../../../../widget/common/ui/Alerts";
import { addModal } from "../../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../../const/ModalKeys";
import ImagePickerPreviewProvider from "../../../widget/ImagePickerPreviewProvider";
import { routeNameForgotPasswordVerification } from "../../../../screens/onboarding/signup/account_verification/SignupAccountVerificationScreen";
import RecoverPasswordProvider from "../../forgot_password/recover_password/RecoverPasswordProvider";
import { phoneMobileDefault } from "../../../../const/MobileConstant";
import { CountryCodeList } from "../../../../model/country_code/CountryCodeModel";

type SignupAccountVerificationTypes = {
  verificationDetail: SignupResult | null;

  pincodeLength: number;
  pincode: string;
  countDownStatus: boolean;
  duration: number;

  changeEmail: string;
  changeEmailIsValid: boolean;

  phoneField: PhoneProps;
  changePhoneIsValid: boolean;

  validateStatus: boolean;

  updateCountDownStatus: (status: boolean) => void;
  sendPin: (code: string) => void;
  resendVerification: () => void;
  handleOnChange: (
    name: string,
    value: string,
    isValid: boolean | undefined
  ) => void;
  submitChangeEmailPhone: () => void;

  updateFileString: (
    fileString: string | null,
    fileDetail: File | null
  ) => void;

  uploadLogoAPI: () => void;

  verificationType: "email" | "phone";
  screenName: string;

  applyCountryCode: (countryCodeDetail: CountryCodeList) => void;
};

type PhoneProps = {
  phone_number: string;
  phone_code: string;
  phone_code_id: string;
};

const SignupAccountVerificationContext = createContext<
  SignupAccountVerificationTypes | undefined
>(undefined);

export const SignupAccountVerificationContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();

  const pincodeLength: number = 4;

  const verificationTypeSession: string =
    SessionStorage.getValue(StorageKeys.verification_type) || "email";

  const verificationType: "email" | "phone" = !["email", "phone"].includes(
    verificationTypeSession
  )
    ? "email"
    : (verificationTypeSession as "email" | "phone"); // 0-email, 1-mobile

  const screenName: string = UtilityHelper.getFullPathName();
  /* const [screenName, setScreenName] = useState<string>(
    UtilityHelper.getFullPathName()
  ); */

  const phoneFieldDefault: PhoneProps = {
    phone_number: "",
    phone_code: phoneMobileDefault.phone_code,
    phone_code_id: phoneMobileDefault.phone_code_id,
  };

  const [verificationDetail, setVerificationDetail] =
    useState<SignupResult | null>(null);

  const [pincode, setPincode] = useState<string>("");
  const [countDownStatus, setCountDownStatus] = useState<boolean>(true);
  const [duration, setDuration] = useState<number>(0);

  const [validateStatus, setValidateStatus] = useState<boolean>(false);

  const [changeEmail, setChangeEmail] = useState<string>("");
  const [changeEmailIsValid, setChangeEmailIsValid] = useState<boolean>(false);

  const [phoneField, setPhoneField] = useState<PhoneProps>(phoneFieldDefault);
  const [changePhoneIsValid, setChangePhoneIsValid] = useState<boolean>(false);

  const [fileDetail, setFileDetail] = useState<File | null>(null);

  const updateCountDownStatus = (status: boolean) => {
    setCountDownStatus(status);
  };

  const checkVericationSession = () => {
    const verificationSessionVal: SignupResult | null =
      SignupAccountVerificationProvider.getVerificationDetail();

    if (verificationSessionVal != null) {
      setPincode(verificationSessionVal?.verif_code!);
      setVerificationDetail(verificationSessionVal!);

      setDuration(getSecondsDiff(verificationSessionVal?.expiry_date!));
    }

    /*  const verificationSession = SessionStorage.getValue(
      StorageKeys.signup_verification_detail
    );

    if (UtilityHelper.isNotEmpty(verificationSession)) {
      const verificationSessionVal: SignupResult =
        JSON.parse(verificationSession);

      setPincode(verificationSessionVal?.verif_code!);
      setVerificationDetail(verificationSessionVal!);

      setDuration(getSecondsDiff(verificationSessionVal?.expiry_date!));

    } */
  };

  const applyCountryCode = (countryCodeDetail: CountryCodeList) => {
    if (countryCodeDetail != null) {
      let phoneFieldTemp = {
        ...phoneField,
        phone_code: countryCodeDetail.country_phonecode!,
        phone_code_id: countryCodeDetail.country_id!,
      };

      setPhoneField(phoneFieldTemp);
    }
  };

  const sendPin = async (code: string) => {
    if (code.length === pincodeLength && countDownStatus) {
      if (code === pincode) {
        const currentSceen: string = UtilityHelper.getFullPathName();

        if (currentSceen === routeNameForgotPasswordVerification) {
          RecoverPasswordProvider.navigateNewPassword();
        } else {
          Loader.modal();
          const profileResponse: SignupProfileModel | null =
            await SignupAccountVerificationProvider.submitPinAPI({
              account_id: verificationDetail?.account_id!,
              verif_code: code,
            });
          Loader.modal(false);

          if (profileResponse != null) {
            confirmToUploadLogo();
          } else {
            ShowToast.warning(AppTranslation.text("something_went_wrong"));
          }
        }
      } else {
        ShowToast.warning(AppTranslation.text("otp_code_invalid"));
      }
    }
  };

  const resendVerification = async () => {
    const currentSceen: string = UtilityHelper.getFullPathName();

    let signupResult: SignupResult | null = verificationDetail;

    if (currentSceen === routeNameForgotPasswordVerification) {
      Loader.modal();
      const signupResultRes: SignupResult | null =
        await SignupAccountVerificationProvider.resendRecoverPasswordAPI(
          verificationDetail?.account_id!,
          verificationType === "email" ? "0" : "1"
        );
      Loader.modal(false);
      signupResult = {
        ...signupResult,
        //...signupResultRes,
        expiry_date: signupResultRes?.expiry_date,
      };
    } else {
      Loader.modal();
      const signupResultRes: SignupResult | null =
        await SignupAccountVerificationProvider.resendVerificationAPI(
          verificationDetail?.account_id!
        );
      Loader.modal(false);

      signupResult = {
        ...signupResult,
        //...signupResultRes,
        expiry_date: signupResultRes?.expiry_date,
      };
    }

    if (signupResult != null) {
      resetPINForm(signupResult);
    } else {
      ShowToast.warning(AppTranslation.text("something_went_wrong"));
    }
  };

  const handleOnChange = (
    fieldName: string,
    fieldValue: string,
    isValid: boolean | undefined
  ) => {
    fieldName = fieldName.toLowerCase();
    let phoneValid: boolean = false;

    if (fieldName === `change_email`) {
      setChangeEmail(fieldValue);
      setChangeEmailIsValid(isValid ?? true);
    } else if (fieldName === `phone_number`) {
      setPhoneField({ ...phoneField, phone_number: fieldValue.trim() });
      if (
        (isValid ?? true) === true &&
        UtilityHelper.isNotEmpty(phoneField.phone_code_id)
      ) {
        phoneValid = true;
      }

      setChangePhoneIsValid(phoneValid);
    }

    setValidateStatus(false);
  };

  const submitChangeEmailPhone = async () => {
    setValidateStatus(true);

    let signupResult: SignupResult | null = null;

    if (verificationType === "phone" && changePhoneIsValid) {
      Loader.modal();
      const signupResultRes: SignupResult | null =
        await SignupAccountVerificationProvider.changePhoneAPI({
          accountId: verificationDetail?.account_id!,
          mobileCode: phoneField.phone_code,
          mobileCodeId: phoneField.phone_code_id,
          mobileNumber: phoneField.phone_number,
        });
      Loader.modal(false);

      if (signupResultRes != null) {
        console.log(signupResultRes);
        signupResult = {
          ...verificationDetail,
          mobile_number: signupResultRes?.mobile_number,
        };
      }
    } else if (verificationType === "email" && changeEmailIsValid) {
      Loader.modal();
      const signupResultRes: SignupResult | null =
        await SignupAccountVerificationProvider.changeEmailAPI(
          verificationDetail?.account_id!,
          changeEmail
        );
      Loader.modal(false);

      if (signupResultRes != null) {
        signupResult = { ...verificationDetail, email: signupResultRes?.email };
      }
    }

    if (signupResult != null) {
      resetPINForm(signupResult);
      SignupAccountVerificationProvider.navigate(
        appRoutes.routeNameSignupVerification
      );
    }

    /* if (changeEmailIsValid) {
      Loader.modal();
      console.log(verificationDetail);
      const signupResultRes: SignupResult | null =
        await SignupAccountVerificationProvider.changeEmailAPI(
          verificationDetail?.account_id!,
          changeEmail
        );
      Loader.modal(false);

      let signupResult: SignupResult = {
        ...verificationDetail,
      };

      if (verificationType === "phone") {
        signupResult = {
          ...signupResult,
          email: signupResultRes?.mobile_number,
        };
      } else {
        signupResult = { ...signupResult, email: signupResultRes?.email };
      }

      if (signupResult != null) {
        resetPINForm(signupResult);
        SignupAccountVerificationProvider.navigate(
          appRoutes.routeNameSignupVerification
        );
      } else {
        ShowToast.warning(AppTranslation.text("something_went_wrong"));
      }
    } */
  };

  const resetPINForm = (signupResult: SignupResult) => {
    if (signupResult != null) {
      dispatch(resetCountDown());

      setPincode(signupResult?.verif_code!);
      setVerificationDetail(signupResult!);
      updateCountDownStatus(true);

      SessionStorage.saveValue(
        StorageKeys.signup_verification_detail,
        JSON.stringify(signupResult)
      );
    }
  };

  const updateFileString = (
    fileString: string | null,
    fileDetail: File | null
  ) => {
    setFileDetail(fileDetail);
  };

  const uploadLogoAPI = async () => {
    if (fileDetail != null) {
      Loader.modal();
      const uploadStatus: boolean =
        await ImagePickerPreviewProvider.uploadImage({
          fileID: "",
          flag: "1",
          file: fileDetail,
        });
      Loader.modal(false);

      if (uploadStatus) {
        dispatch(
          addModal({
            id: ModalKeys.signup_welcom_message,
            size: "md",
            hideHeader: true,
            static: true,
          })
        );
      }
    }
  };

  const confirmToUploadLogo = () => {
    AlertConfirm({
      title: "do_you_wish_profile_picture",
      textCancel: "no",
      textConfirm: "yes",
      onCancel: () => {
        dispatch(
          addModal({
            id: ModalKeys.signup_welcom_message,
            size: "md",
            hideHeader: true,
            static: true,
          })
        );
      },
      onConfirm: () => {
        SignupAccountVerificationProvider.navigate(
          appRoutes.routeNameSignupUploadLogo
        );
      },
    });
  };

  const resetForm = () => {
    setPincode("");
    setChangeEmail("");
    setFileDetail(null);
  };

  useEffect(() => {
    resetForm();
    checkVericationSession();
  }, []);

  return (
    <SignupAccountVerificationContext.Provider
      value={{
        verificationDetail,
        sendPin,
        resendVerification,
        updateCountDownStatus,
        pincodeLength,
        pincode,
        countDownStatus,
        duration,

        changeEmail,
        changeEmailIsValid,

        phoneField,
        changePhoneIsValid,

        validateStatus,

        handleOnChange,

        submitChangeEmailPhone,

        updateFileString,
        uploadLogoAPI,

        verificationType,
        screenName,

        applyCountryCode,
      }}
    >
      {children}
    </SignupAccountVerificationContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const SignupAccountVerificationContextImport = () => {
  const context = useContext(SignupAccountVerificationContext);
  if (!context) {
    throw new Error(
      "SignupAccountVerificationContextImport must be used within a SignupAccountVerificationContextProvider"
    );
  }
  return context;
};
